import {
	AfterViewInit,
	Component,
	ElementRef,
	Inject,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { createId } from '@core/util/file';
import { DATE_FORMAT } from '@models/businesses';
import {
	DefaultMatterAdjustmentDeposit,
	DefaultMatterAdjustmentDepositRecord,
	MatterAdjustmentDeposit,
} from '@models/matter';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { AdjustmentDefaultDetailsSelectors } from '@state/global/adjustment-details';
import { AdjustmentsDispatch } from '@state/matter/adjustments';
import { AdjustmentsSelector } from '@state/matter/adjustments/adjustments.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import format from 'date-fns/format';
import { cloneDeep } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { ICON } from '@core/util/icon';

export interface DepositDialogData {
	matterId: string;
}

@UntilDestroy()
@Component({
	selector: 'app-deposit-dialog',
	templateUrl: 'deposit-dialog.component.html',
	styleUrls: ['deposit-dialog.component.scss'],
	standalone: false,
})
export class DepositDialogComponent implements OnInit, AfterViewInit {
	adjustment: MatterAdjustmentDeposit;
	agreementDate: string;
	public closingDate: string;

	@Select(SelectedMatter.agreementDate) agreementDate$: Observable<string>;
	@Select(AdjustmentsSelector.depositAdjustment)
	depositAdjustment$: Observable<MatterAdjustmentDeposit>;
	@Select(SelectedMatter.closingDate) closingDate$: Observable<string>;
	@SelectSnapshot(AdjustmentDefaultDetailsSelectors.adjustmentDetailsDeposit)
	public adjustmentDetails: boolean;

	readonly ICON = ICON;
	total: number;

	@ViewChild('depositList') private depositList: ElementRef;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: DepositDialogData,
		private dialogRef: MatDialogRef<DepositDialogComponent>,
		private adjustmentDispatch: AdjustmentsDispatch,
	) {}

	ngOnInit(): void {
		this.total = 0;
		this.closingDate$.pipe(untilDestroyed(this)).subscribe((_closingDate) => {
			this.closingDate = _closingDate;
		});
		this.adjustment = cloneDeep({
			...DefaultMatterAdjustmentDeposit,
			id: createId(),
			details: this.adjustmentDetails,
		});
	}

	ngAfterViewInit(): void {
		this.dialogRef.addPanelClass('deposit-dialog');

		combineLatest([this.depositAdjustment$, this.agreementDate$])
			.pipe(untilDestroyed(this))
			.subscribe(([depositAdjustment, agreementDate]) => {
				this.agreementDate = agreementDate;
				if (depositAdjustment) {
					this.adjustment = cloneDeep(depositAdjustment);
				}

				if (this.adjustment?.deposits?.length === 0) {
					this.addDeposit();
				}

				if (this.adjustment?.deposits?.length > 0) {
					this.scrollToLastDeposit();
				}
			});
	}

	addDeposit(): void {
		this.adjustment.deposits.push({
			...DefaultMatterAdjustmentDepositRecord,
			id: createId(),
			date: this.agreementDate ?? format(new Date(), DATE_FORMAT),
		});
		this.scrollToLastDeposit();
	}

	cancelModal(): void {
		this.dialogRef.close();
	}

	changed(): void {
		this.total =
			this.adjustment.deposits.reduce((prev, cur) => +prev + +cur.amount, 0) *
			-1;
	}

	removeDeposit(index: number): void {
		this.adjustment.deposits.splice(index, 1);
	}

	saveModal(): void {
		this.adjustmentDispatch.Set(this.adjustment);
		this.dialogRef.close();
	}

	depositCompoundIndex(index: number): number {
		return index;
	}

	private scrollToLastDeposit(): void {
		window.setTimeout(
			() =>
				(this.depositList.nativeElement.scrollTop =
					this.depositList.nativeElement.scrollHeight + 250),
			100,
		);
	}
}
